import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SaveIcon from "@mui/icons-material/Save";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../../../../components/topbar/TopBar";
import { createTarifaZoneUseCase } from "../domain/usecases/createTarifaZoneUseCase";
import { deleteTarifaZoneUseCase } from "../domain/usecases/deleteTarifaZoneUseCase";
import { getTarifaZoneByIdUseCase } from "../domain/usecases/getTarifaZoneByIdUseCase";
import { getTarifaZoneListUseCase } from "../domain/usecases/getTarifaZoneListUseCase";
import { updateTarifaZoneUseCase } from "../domain/usecases/updateTarifaZoneUseCase";

const TEMPORARY_ZONES = [
  {
    value: "zona-norte",
    label: "Zona Norte",
    latitude: -12.0464,
    longitude: -77.0428,
  },
  {
    value: "zona-sur",
    label: "Zona Sur",
    latitude: -12.1406,
    longitude: -77.0189,
  },
  {
    value: "zona-este",
    label: "Zona Este",
    latitude: -12.0553,
    longitude: -76.9667,
  },
  {
    value: "zona-oeste",
    label: "Zona Oeste",
    latitude: -12.0719,
    longitude: -77.1076,
  },
];

function createEmptyForm() {
  return {
    origin: "",
    destination: "",
    price: "",
  };
}

function findZone(value) {
  return TEMPORARY_ZONES.find((zone) => zone.value === value) || null;
}

function findZoneValueByLabel(label) {
  return TEMPORARY_ZONES.find((zone) => zone.label === label)?.value || "";
}

function createPayload(form) {
  const origin = findZone(form.origin);
  const destination = findZone(form.destination);

  return {
    origin: origin?.label || "",
    originLat: origin ? Number(origin.latitude) : null,
    originLng: origin ? Number(origin.longitude) : null,
    destination: destination?.label || "",
    destinationLat: destination ? Number(destination.latitude) : null,
    destinationLng: destination ? Number(destination.longitude) : null,
    price: Number(form.price),
  };
}

function createFormFromRecord(record) {
  if (!record) {
    return createEmptyForm();
  }

  return {
    origin: findZoneValueByLabel(record.origin),
    destination: findZoneValueByLabel(record.destination),
    price:
      record.price === null || record.price === undefined
        ? ""
        : String(record.price),
  };
}

function isFormReady(form) {
  return (
    Boolean(findZone(form.origin)) &&
    Boolean(findZone(form.destination)) &&
    form.price !== "" &&
    !Number.isNaN(Number(form.price)) &&
    Number(form.price) >= 0
  );
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return response?.success === true || message.includes("success");
}

function formatPrice(price) {
  return Number(price).toFixed(2);
}

function TarifaZoneForm({
  form,
  onChange,
  onSubmit,
  submitLabel,
  loadingLabel,
  disabled = false,
}) {
  const canSubmit = useMemo(() => isFormReady(form), [form]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    onChange({
      ...form,
      [name]: value,
    });
  };

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        select
        label="Origen"
        name="origin"
        value={form.origin}
        onChange={handleChange}
      >
        {TEMPORARY_ZONES.map((zone) => (
          <MenuItem key={zone.value} value={zone.value}>
            {zone.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        select
        label="Destino"
        name="destination"
        value={form.destination}
        onChange={handleChange}
      >
        {TEMPORARY_ZONES.map((zone) => (
          <MenuItem key={zone.value} value={zone.value}>
            {zone.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        type="number"
        label="Precio"
        name="price"
        value={form.price}
        onChange={handleChange}
        inputProps={{ min: "0", step: "0.01" }}
      />
      <Button
        fullWidth
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={onSubmit}
        disabled={!canSubmit || disabled}
        sx={{
          mt: 1,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 700,
          bgcolor: "#165c53",
          color: "#fff",
          "&:hover": {
            bgcolor: "#12483f",
          },
        }}
      >
        {disabled ? loadingLabel : submitLabel}
      </Button>
    </Stack>
  );
}

export const TarifaZoneCreatePage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(createEmptyForm());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(createEmptyForm());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const loadRatesZone = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      const rates = await getTarifaZoneListUseCase();
      setRecords(rates);
    } catch (error) {
      setRecords([]);
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo cargar la lista de tarifas.",
      });
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadRatesZone();
  }, []);

  const handleCreate = async () => {
    const payload = createPayload(form);

    try {
      setSaving(true);
      const response = await createTarifaZoneUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo crear la tarifa de zona a zona.");
      }

      setForm(createEmptyForm());
      setMessage({
        open: true,
        type: "success",
        text: "Tarifa de zona a zona creada correctamente.",
      });
      await loadRatesZone({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo crear la tarifa de zona a zona.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenMenu = (event, record) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleOpenEdit = async () => {
    try {
      const record = await getTarifaZoneByIdUseCase(selectedRecord.id);
      setSelectedRecord(record);
      setEditForm(createFormFromRecord(record));
      setEditOpen(true);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo leer la tarifa seleccionada.",
      });
    } finally {
      handleCloseMenu();
    }
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setSelectedRecord(null);
    setEditForm(createEmptyForm());
  };

  const handleUpdate = async () => {
    const payload = {
      id: selectedRecord.id,
      ...createPayload(editForm),
    };

    try {
      setSaving(true);
      const response = await updateTarifaZoneUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo actualizar la tarifa de zona a zona.");
      }

      handleCloseEdit();
      setMessage({
        open: true,
        type: "success",
        text: "Tarifa de zona a zona actualizada correctamente.",
      });
      await loadRatesZone({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text:
          error.message || "No se pudo actualizar la tarifa de zona a zona.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = () => {
    setDeleteOpen(true);
    handleCloseMenu();
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedRecord(null);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await deleteTarifaZoneUseCase(selectedRecord.id);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo eliminar la tarifa de zona a zona.");
      }

      handleCloseDelete();
      setMessage({
        open: true,
        type: "success",
        text: "Tarifa de zona a zona eliminada correctamente.",
      });
      await loadRatesZone({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo eliminar la tarifa de zona a zona.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 960,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                >
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{ color: "#fff !important" }}
                  >
                    CREAR TARIFA PARA ZONA A ZONA
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/admin/zona-especial")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      fontWeight: 700,
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Crear Zona Especial
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                <Stack spacing={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      mx: "auto",
                      width: "100%",
                      maxWidth: 560,
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      p: { xs: 1, md: 2 },
                    }}
                  >
                    <TarifaZoneForm
                      form={form}
                      onChange={setForm}
                      onSubmit={handleCreate}
                      submitLabel="Crear"
                      loadingLabel="Creando..."
                      disabled={saving}
                    />
                  </Paper>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      overflowX: "auto",
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: "#f4faf7",
                            "& th": {
                              fontWeight: 800,
                              color: "#12483f",
                            },
                          }}
                        >
                          <TableCell>Origen</TableCell>
                          <TableCell>Destino</TableCell>
                          <TableCell>Precio</TableCell>
                          <TableCell align="right">Opciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                                <CircularProgress />
                                <Typography color="text.secondary">
                                  Cargando tarifas...
                                </Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ) : records.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography sx={{ py: 3 }} color="text.secondary">
                                No hay tarifas registradas.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          records.map((record) => (
                            <TableRow key={record.id} hover>
                              <TableCell>{record.origin}</TableCell>
                              <TableCell>{record.destination}</TableCell>
                              <TableCell>{formatPrice(record.price)}</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  aria-label="Opciones"
                                  onClick={(event) =>
                                    handleOpenMenu(event, record)
                                  }
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Box>
            </Paper>
          </Stack>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleCloseMenu}
          >
            <MenuItem onClick={handleOpenEdit}>editar</MenuItem>
            <MenuItem onClick={handleOpenDelete}>eliminar</MenuItem>
          </Menu>

          <Dialog
            fullWidth
            maxWidth="sm"
            open={editOpen}
            onClose={handleCloseEdit}
          >
            <DialogTitle>Editar tarifa de zona a zona</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <TarifaZoneForm
                  form={editForm}
                  onChange={setEditForm}
                  onSubmit={handleUpdate}
                  submitLabel="actualizar"
                  loadingLabel="actualizando..."
                  disabled={saving}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEdit}>cancelar</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={deleteOpen} onClose={handleCloseDelete}>
            <DialogTitle>
              Deseas Eliminar esta Tarifa de Zona a zona?
            </DialogTitle>
            <DialogActions>
              <Button onClick={handleCloseDelete} disabled={deleting}>
                cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "eliminando..." : "aceptar"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};
